import React, {useMemo, useState, useCallback} from "react";
import {useTranslation} from "react-i18next";
import Content from "./Content";
import {ChallengeInputMode, ChoiceUserInputAction, UserInputAction, ChallengeMode} from "../../../../schema";
import {PlayerReduxState, UserInputDisplayMode} from "../../../../redux/player/types";
import Phrase, {PhraseVariant} from "../../../Phrase";
import UserInputHelpText from "../../UserInputHelpText";
import {useDispatch, useSelector} from "react-redux";
import {getChallenge, getDialogueHistory} from "../../../../redux/player/selectors";
import selectInput from "../../../../redux/player/actions/selectInput";
import {isEqual} from "lodash";
import InitAgentActionContent from "./InitAgentActionContent";

type UserInputActionContentProps = Omit<React.ComponentProps<typeof Content>, "title" | "variant" | "children"> & {
  action:
    | Pick<UserInputAction, "questionText" | "hintText" | "referenceText">
    | Pick<ChoiceUserInputAction, "questionText" | "hintText" | "isMultipleChoice">;
  phraseMode: PhraseVariant | undefined;
  displayMode: UserInputDisplayMode | undefined;
  recognizedText?: string;
  feedbackText?: string;
  isHistoryOpen?: boolean;
};

const UserInputActionContent = ({
  action,
  phraseMode,
  displayMode,
  recognizedText,
  feedbackText: feedbackTextProp,
  isHistoryOpen,
  ...props
}: UserInputActionContentProps) => {
  const {t} = useTranslation();

  const dispatch = useDispatch();
  const {
    challenge,
    feedbackText: feedbackTextFromState,
    isRecognizing,
    dialogueHistory,
  } = useSelector(UserInputActionContent.selector, isEqual);
  const isHistoryMode = recognizedText != null;
  const displayFeedbackText = isHistoryMode ? feedbackTextProp : feedbackTextFromState;
  const suppressEchoNpcFeedback = useMemo(() => {
    if (!isHistoryOpen || !displayFeedbackText || dialogueHistory.length === 0) {
      return false;
    }
    const last = dialogueHistory[dialogueHistory.length - 1];
    if (last.action.__typename !== "AvatarAction") {
      return false;
    }
    return true;
  }, [isHistoryOpen, displayFeedbackText, dialogueHistory]);
  const isInputSelectable =
    challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES ||
    challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST ||
    (challenge.mode === ChallengeMode.CHOICE_BASED_STUDY && challenge.selectableInputs);
  const [isClickRegistered, registerClick] = useState(false);
  const isTextInput = challenge.inputMode === ChallengeInputMode.TEXT;

  const selectInputHandler = useCallback(
    (branchIndex: number) => {
      if (isInputSelectable && !isClickRegistered) {
        registerClick(true);
        dispatch(selectInput(branchIndex));
        if (challenge.mode === ChallengeMode.CHOICE_BASED_STUDY && challenge.selectableInputs) {
          registerClick(false);
        }
      }
    },
    [dispatch, isInputSelectable, isClickRegistered, challenge.mode, challenge.selectableInputs],
  );

  const onContentClick = useCallback(() => {
    const branchIndex = 0;
    if (isInputSelectable && !isClickRegistered) {
      selectInputHandler(branchIndex);
      dispatch(selectInput(branchIndex));
    }
  }, [isInputSelectable, isClickRegistered, selectInputHandler, dispatch]);

  const displayVariant = useMemo(() => {
    if (isTextInput) {
      return UserInputDisplayMode.TEXT_INPUT;
    } else {
      return UserInputDisplayMode.EXPECTED_TEXT;
    }
  }, [isTextInput]);

  const textToDisplay = isHistoryMode
    ? recognizedText!
    : action.hintText !== ""
      ? action.hintText
      : challenge.mode === ChallengeMode.COMPETENCE_TEST
        ? action.questionText
        : "referenceText" in action
          ? action.referenceText
          : "";

  const isQuestion =
    challenge.mode === ChallengeMode.COMPETENCE_TEST &&
    action.hintText === "" &&
    action.questionText &&
    displayMode === UserInputDisplayMode.EXPECTED_TEXT;

  const contentVariant = isQuestion
    ? "question"
    : displayMode === UserInputDisplayMode.EXPECTED_TEXT
      ? "user"
      : "userHint";

  const displayUserInput = !(challenge.mode === ChallengeMode.RPG_MODE) || !isRecognizing;

  return (
    <>
      <InitAgentActionContent isHistoryOpen={isHistoryOpen} />
      {displayFeedbackText && !suppressEchoNpcFeedback && (
        <Content.Container appear>
          <Content variant="avatar">{displayFeedbackText}</Content>
        </Content.Container>
      )}
      {displayUserInput && (
        <Content
          title={
            !isHistoryMode ? (
              <UserInputHelpText
                displayMode={displayVariant ?? displayMode}
                isQuestion={(isQuestion && isTextInput) || false}
              />
            ) : undefined
          }
          onClick={isHistoryMode ? undefined : onContentClick}
          variant={contentVariant}
          {...props}
        >
          {displayMode === UserInputDisplayMode.EXPECTED_TEXT || isHistoryMode ? (
            <Phrase
              text={textToDisplay
                .replace("<entername>", t("player.enterName"))
                .replace("<name>", t("player.sessionUserName"))}
              variant={phraseMode ?? PhraseVariant.DEFAULT}
            />
          ) : (
            <Phrase text={action.hintText} variant={PhraseVariant.DEFAULT} italic />
          )}
        </Content>
      )}
    </>
  );
};

export default UserInputActionContent;

UserInputActionContent.selector = (state: PlayerReduxState) => {
  return {
    challenge: getChallenge(state),
    feedbackText: state.adaptiveInput?.feedbackText,
    audioManager: state.audioManager,
    isRecognizing: state.isRecognizing,
    dialogueHistory: getDialogueHistory(state),
  };
};
